import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
    addWorkorderManagementBatchDays,
    deleteWorkorderManagementBatch,
    deleteWorkorderManagementBob,
    getWorkorderManagementBatches,
    getWorkorderManagementBobs,
    getWorkorderManagementBoms,
    getWorkorderManagementData,
    getWorkorderManagementMaterial,
    getWorkorderManagementOperations,
    saveWorkordeManagementMaterial,
    saveWorkorderManagementBatch,
    addWorkorderManagementBob,
    changeWorkorderManagementBobPriority
} from "../../aplApi/workorderManagementHelper";
import { ReduxStateWorkorderManagement } from "../../types/redux/reduxStateWorkorderManagement";
import { initialStateWorkorderManagement } from "../defaultState";
import { getWorkorderItemsByLineId } from "../../aplApi/workorderItemsHelper";
import { getPrintersOfflineBySiteId } from "../../aplApi/printersHelper";
import { getWorkorderLabel, saveWorkorderLabel } from "../../aplApi/workorderLabelsHelper";
import { WorkorderLabel } from "../../types/workorderLabel";
import { WorkorderManagementMaterial } from "../../types/workorderManagementMaterial";
import { getAplError } from "../../aplApi/exceptionHelper";
import { WorkorderManagementBatch } from "../../types/workorderManagementBatch";
import { getNextWorkorderManagementBatch } from "../../aplUtils/batchesHelper";
import { IRootStateFromReducer } from "../store";
import { WorkorderManagementBob } from "../../types/workorderManagementBob";

export const workorderManagementData = createAsyncThunk(
    "workorderManagement/workorderManagementData",
    async (workorderId: string, thunkApi) => {
        try {
            return (await getWorkorderManagementData(workorderId)).data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(getAplError(error, error.response.data));
        }
    }
);
export const workorderManagementMaterial = createAsyncThunk(
    "workorderManagement/workorderManagementMaterials",
    async (materialId: string, thunkApi) => {
        try {
            return (await getWorkorderManagementMaterial(materialId)).data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(getAplError(error, error.response.data));
        }
    }
);

export const saveWorkordeManagementMaterialRedux = createAsyncThunk(
    "workorderManagement/saveWorkordeManagementMaterialRedux",
    async (material: WorkorderManagementMaterial, thunkApi) => {
        try {
            return (await saveWorkordeManagementMaterial(material)).data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(getAplError(error, error.response.data));
        }
    }
);

export const workorderManagementOperations = createAsyncThunk(
    "workorderManagement/workorderManagementOperations",
    async (workorderId: string, thunkApi) => {
        try {
            return (await getWorkorderManagementOperations(workorderId)).data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(getAplError(error, error.response.data));
        }
    }
);
export const workorderManagementBoms = createAsyncThunk(
    "workorderManagement/workorderManagementBoms",
    async (workorderId: string, thunkApi) => {
        try {
            return (await getWorkorderManagementBoms(workorderId)).data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(getAplError(error, error.response.data));
        }
    }
);
export const workorderManagementBatches = createAsyncThunk(
    "workorderManagement/workorderManagementBatches",
    async (workorderId: string, thunkApi) => {
        try {
            return (await getWorkorderManagementBatches(workorderId)).data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(getAplError(error, error.response.data));
        }
    }
);
export const workorderManagementSaveBatch = createAsyncThunk(
    "workorderManagement/workorderManagementSaveBatch",
    async (workorderBatch: WorkorderManagementBatch, thunkApi) => {
        try {
            await saveWorkorderManagementBatch(workorderBatch);
            return (await getWorkorderManagementBatches(workorderBatch.workOrderId)).data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(getAplError(error, error.response.data));
        }
    }
);

export const workorderManagementAddMultipleBatches = createAsyncThunk(
    "workorderManagement/workorderManagementAddMultipleBatches",
    async (daysCount: number, thunkApi) => {
        let state = thunkApi.getState() as IRootStateFromReducer;
        let workorderData = state.workorderManagement.orderData;

        try {
            if (workorderData == null) throw Error("WorkorderData is null");
            await addWorkorderManagementBatchDays(workorderData.workorderId, daysCount);
            return (await getWorkorderManagementBatches(workorderData.workorderId)).data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(getAplError(error, error.response.data));
        }
    }
);

export const workorderManagementDeleteBatch = createAsyncThunk(
    "workorderManagement/workorderManagementDeleteBatch",
    async (workorderBatch: WorkorderManagementBatch, thunkApi) => {
        try {
            await deleteWorkorderManagementBatch(workorderBatch);
            return (await getWorkorderManagementBatches(workorderBatch.workOrderId)).data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(getAplError(error, error.response.data));
        }
    }
);

export const getWorkorderManagementBobsRedux = createAsyncThunk(
    "workorderManagement/getWorkorderManagementBobsRedux",
    async (workorderId: string) => (await getWorkorderManagementBobs(workorderId)).data
);

export const deleteWorkorderManagementBobRedux = createAsyncThunk(
    "workorderManagement/deleteWorkorderManagementBobRedux",
    async (bob: WorkorderManagementBob, thunkApi) => {
        try {
            await deleteWorkorderManagementBob(bob.woBobId);
            return (await getWorkorderManagementBobs(bob.workorderId)).data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(getAplError(error, error.response.data));
        }
    }
);

export const changeWorkorderManagementBobPriorityRedux = createAsyncThunk(
    "workorderManagement/changeWorkorderManagementBobPriorityRedux",
    async (data: { bob: WorkorderManagementBob; offset: number }, thunkApi) => {
        try {
            await changeWorkorderManagementBobPriority(data.bob, data.offset);
            return (await getWorkorderManagementBobs(data.bob.workorderId)).data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(getAplError(error, error.response.data));
        }
    }
);

export const addWorkorderManagementBobRedux = createAsyncThunk(
    "workorderManagement/addWorkorderManagementBobRedux",
    async (data: { siteId: number; batch: number; workorderId: string }, thunkApi) => {
        try {
            await addWorkorderManagementBob(data);
            return (await getWorkorderManagementBobs(data.workorderId)).data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(getAplError(error, error.response.data));
        }
    }
);

export const alternatingWorkordersData = createAsyncThunk(
    "workorderManagement/alternatingWorkordersData",
    async (lineId: number, thunkApi) => {
        try {
            return (await getWorkorderItemsByLineId(lineId)).data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(getAplError(error, error.response.data));
        }
    }
);

export const testPrinters = createAsyncThunk("workorderManagement/testPrinters", async (siteId: number, thunkApi) => {
    try {
        return (await getPrintersOfflineBySiteId(siteId)).data;
    } catch (error: any) {
        return thunkApi.rejectWithValue(getAplError(error, error.response.data));
    }
});

export const getWorkorderLabelRedux = createAsyncThunk(
    "workorderManagement/getWorkorderLabelRedux",
    async (workorderId: string, thunkApi) => {
        try {
            return (await getWorkorderLabel(workorderId)).data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(getAplError(error, error.response.data));
        }
    }
);

export const saveWorkorderLabelRedux = createAsyncThunk(
    "workorderManagement/saveWorkorderLabelRedux",
    async (workorderLabel: WorkorderLabel, thunkApi) => {
        try {
            return (await saveWorkorderLabel(workorderLabel.workorderId, workorderLabel)).data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(getAplError(error, error.response.data));
        }
    }
);

const workorderManagement = createSlice({
    name: "workorderManagement",
    initialState: initialStateWorkorderManagement,
    reducers: {
        onClearWorkorderManagementData: function () {
            return initialStateWorkorderManagement;
        },
        onSetSelectedOperations: (state, action) => ({ ...state, orderOperationsSelected: action.payload }),
        onSetSelectedBatches: (state, action) => ({ ...state, orderBatchesSelected: action.payload }),
        onSetSelectedBobs: (state, action) => ({ ...state, orderBobsSelected: action.payload }),
        onSetSelectedBoms: (state, action) => ({ ...state, orderBomsSelected: action.payload }),
        onSetWorkorderMainData: (state, action) => ({ ...state, orderData: action.payload } as ReduxStateWorkorderManagement),
        onSetWorkorderMaterialData: (state, action) => ({ ...state, orderMaterialData: action.payload } as ReduxStateWorkorderManagement),
        onSetWorkorderMaterialDataDirty: (state, action) =>
            ({ ...state, orderMaterialDataDirty: action.payload } as ReduxStateWorkorderManagement),
        onSetWorkorderDataDirty: (state, action) => ({ ...state, orderDataDirty: action.payload } as ReduxStateWorkorderManagement),
        onSetWorkorderOperationsData: (state, action) =>
            ({ ...state, orderOperationsData: action.payload } as ReduxStateWorkorderManagement),
        onSetWorkorderBomsData: (state, action) => ({ ...state, orderBomsData: action.payload } as ReduxStateWorkorderManagement),
        onSetWorkorderBatchesData: (state, action) => ({ ...state, orderBatchesData: action.payload } as ReduxStateWorkorderManagement),
        onUpdateWorkorderBatch: (state, action) =>
            ({
                ...state,
                orderBatchesData: [
                    ...state.orderBatchesData.filter(x => x.workOrderBatchId !== action.payload.workOrderBatchId),
                    action.payload
                ]
            } as ReduxStateWorkorderManagement),
        onAddWorkorderBatch: state => {
            let newBatch = getNextWorkorderManagementBatch(state.orderBatchesData, state.orderData!);
            return {
                ...state,
                orderBatchesData: [newBatch, ...state.orderBatchesData],
                orderBatchesSelected: [newBatch]
            } as ReduxStateWorkorderManagement;
        },
        onSetWorkorderBobsData: (state, action) => ({ ...state, orderBobsData: action.payload } as ReduxStateWorkorderManagement),
        onClearAlternatingWorkorderData: (state, action) => ({
            ...state,
            alternatingWokrordersData: [],
            alternatingWokrordersDataLoading: false,
            alternatingWokrordersDataError: null
        }),

        onSetBomsAdding: (state, action) => ({ ...state, orderBomsAdding: action.payload } as ReduxStateWorkorderManagement),
        onSetBatchesAdding: (state, action) => ({ ...state, orderBatchesAdding: action.payload } as ReduxStateWorkorderManagement),
        onSetBatchesAddingMultiple: (state, action) =>
            ({ ...state, orderBatchesAddingMultiple: action.payload } as ReduxStateWorkorderManagement),
        onSetBatchesAddingMultipleDays: (state, action) =>
            ({ ...state, orderBatchesAddingMultipleDays: action.payload } as ReduxStateWorkorderManagement),
        onSetTestPrintLabelId: (state, action) => ({ ...state, testPrintSelectedLabelId: action.payload } as ReduxStateWorkorderManagement),
        onSetTestPrintPrinterId: (state, action) =>
            ({ ...state, testPrintSelectedPrinterId: action.payload } as ReduxStateWorkorderManagement),
        onSetWorkorderLabels: (state, action) =>
            ({ ...state, labelsData: action.payload, labelsDirty: true } as ReduxStateWorkorderManagement),
        setOrderBobAddingRedux: (state, action) => ({ ...state, orderBobAdding: action.payload } as ReduxStateWorkorderManagement)
    },
    extraReducers: builder => {
        builder
            .addCase(workorderManagementData.pending, state => {
                return {
                    ...state,
                    orderDataLoading: true
                } as ReduxStateWorkorderManagement;
            })
            .addCase(workorderManagementData.fulfilled, (state, action) => {
                return {
                    ...state,
                    orderDataLoading: false,
                    orderData: action.payload,
                    orderDataError: null,
                    orderDataDirty: false
                } as ReduxStateWorkorderManagement;
            })
            .addCase(workorderManagementData.rejected, (state, action) => {
                return {
                    ...state,
                    orderData: null,
                    orderDataLoading: false,
                    orderDataError: action.error.code === "ERR_CANCELED" ? null : action.payload
                } as ReduxStateWorkorderManagement;
            });

        builder
            .addCase(workorderManagementMaterial.pending, state => {
                return {
                    ...state,
                    ordermaterialDataLoading: true
                } as ReduxStateWorkorderManagement;
            })
            .addCase(workorderManagementMaterial.fulfilled, (state, action) => {
                return {
                    ...state,
                    materialorderDataLoading: false,
                    orderMaterialData: action.payload,
                    orderMaterialDataError: null,
                    orderMaterialDataDirty: false
                } as ReduxStateWorkorderManagement;
            })
            .addCase(workorderManagementMaterial.rejected, (state, action) => {
                return {
                    ...state,
                    orderMaterialData: null,
                    ordermaterialDataLoading: false,
                    orderMaterialDataError: action.error.code === "ERR_CANCELED" ? null : action.payload
                } as ReduxStateWorkorderManagement;
            });

        builder
            .addCase(saveWorkordeManagementMaterialRedux.pending, state => {
                return {
                    ...state,
                    ordermaterialDataLoading: true
                } as ReduxStateWorkorderManagement;
            })
            .addCase(saveWorkordeManagementMaterialRedux.fulfilled, (state, action) => {
                return {
                    ...state,
                    materialorderDataLoading: false,
                    orderMaterialData: action.payload,
                    orderMaterialDataError: null,
                    orderMaterialDataDirty: false
                } as ReduxStateWorkorderManagement;
            })
            .addCase(saveWorkordeManagementMaterialRedux.rejected, (state, action) => {
                return {
                    ...state,
                    orderMaterialData: null,
                    ordermaterialDataLoading: false,
                    orderMaterialDataError: action.error.code === "ERR_CANCELED" ? null : action.payload
                } as ReduxStateWorkorderManagement;
            });

        builder
            .addCase(workorderManagementOperations.pending, state => {
                return {
                    ...state,
                    orderOperationsDataLoading: true
                } as ReduxStateWorkorderManagement;
            })
            .addCase(workorderManagementOperations.fulfilled, (state, action) => {
                return {
                    ...state,
                    orderOperationsDataLoading: false,
                    orderOperationsData: action.payload,
                    orderOperationsDataError: null
                } as ReduxStateWorkorderManagement;
            })
            .addCase(workorderManagementOperations.rejected, (state, action) => {
                return {
                    ...state,
                    orderOperationsData: [],
                    orderOperationsDataLoading: false,
                    orderOperationsDataError: action.error.code === "ERR_CANCELED" ? null : action.payload
                } as ReduxStateWorkorderManagement;
            });

        builder
            .addCase(workorderManagementBoms.pending, state => {
                return {
                    ...state,
                    orderBomsDataLoading: true
                } as ReduxStateWorkorderManagement;
            })
            .addCase(workorderManagementBoms.fulfilled, (state, action) => {
                return {
                    ...state,
                    orderBomsDataLoading: false,
                    orderBomsData: action.payload,
                    orderBomsDataError: null
                } as ReduxStateWorkorderManagement;
            })
            .addCase(workorderManagementBoms.rejected, (state, action) => {
                return {
                    ...state,
                    orderBomsData: [],
                    orderBomsDataLoading: false,
                    orderBomsDataError: action.error.code === "ERR_CANCELED" ? null : action.payload
                } as ReduxStateWorkorderManagement;
            });

        builder
            .addCase(workorderManagementBatches.pending, state => {
                return {
                    ...state,
                    orderBatchesDataLoading: true,
                    orderBatchesDataError: null
                } as ReduxStateWorkorderManagement;
            })
            .addCase(workorderManagementBatches.fulfilled, (state, action) => {
                return {
                    ...state,
                    orderBatchesDataLoading: false,
                    orderBatchesData: action.payload,
                    orderBatchesDataError: null,
                    orderBatchesSelected: []
                } as ReduxStateWorkorderManagement;
            })
            .addCase(workorderManagementBatches.rejected, (state, action) => {
                return {
                    ...state,
                    orderBatchesData: [],
                    orderBatchesDataLoading: false,
                    orderBatchesDataError: action.error.code === "ERR_CANCELED" ? null : action.payload
                } as ReduxStateWorkorderManagement;
            });

        builder
            .addCase(workorderManagementSaveBatch.pending, state => {
                return {
                    ...state,
                    orderBatchesDataLoading: true,
                    orderBatchesDataError: null
                } as ReduxStateWorkorderManagement;
            })
            .addCase(workorderManagementSaveBatch.fulfilled, (state, action) => {
                return {
                    ...state,
                    orderBatchesDataLoading: false,
                    orderBatchesData: action.payload,
                    orderBatchesDataError: null,
                    orderBatchesSelected: []
                } as ReduxStateWorkorderManagement;
            })
            .addCase(workorderManagementSaveBatch.rejected, (state, action) => {
                return {
                    ...state,
                    // orderBatchesData: [],
                    orderBatchesDataLoading: false,
                    orderBatchesDataError: action.error.code === "ERR_CANCELED" ? null : action.payload
                } as ReduxStateWorkorderManagement;
            });

        builder
            .addCase(workorderManagementAddMultipleBatches.pending, state => {
                return {
                    ...state,
                    orderBatchesDataLoading: true,
                    orderBatchesDataError: null
                } as ReduxStateWorkorderManagement;
            })
            .addCase(workorderManagementAddMultipleBatches.fulfilled, (state, action) => {
                return {
                    ...state,
                    orderBatchesDataLoading: false,
                    orderBatchesData: action.payload,
                    orderBatchesDataError: null,
                    orderBatchesSelected: []
                } as ReduxStateWorkorderManagement;
            })
            .addCase(workorderManagementAddMultipleBatches.rejected, (state, action) => {
                return {
                    ...state,
                    // orderBatchesData: [],
                    orderBatchesDataLoading: false,
                    orderBatchesDataError: action.error.code === "ERR_CANCELED" ? null : action.payload
                } as ReduxStateWorkorderManagement;
            });

        builder
            .addCase(workorderManagementDeleteBatch.pending, state => {
                return {
                    ...state,
                    orderBatchesDataLoading: true,
                    orderBatchesDataError: null
                } as ReduxStateWorkorderManagement;
            })
            .addCase(workorderManagementDeleteBatch.fulfilled, (state, action) => {
                return {
                    ...state,
                    orderBatchesDataLoading: false,
                    orderBatchesData: action.payload,
                    orderBatchesDataError: null,
                    orderBatchesSelected: []
                } as ReduxStateWorkorderManagement;
            })
            .addCase(workorderManagementDeleteBatch.rejected, (state, action) => {
                return {
                    ...state,
                    orderBatchesData: [],
                    orderBatchesDataLoading: false,
                    orderBatchesDataError: action.error.code === "ERR_CANCELED" ? null : action.payload
                } as ReduxStateWorkorderManagement;
            });

        builder
            .addCase(getWorkorderManagementBobsRedux.pending, state => {
                return {
                    ...state,
                    orderBobsDataLoading: true
                } as ReduxStateWorkorderManagement;
            })
            .addCase(getWorkorderManagementBobsRedux.fulfilled, (state, action) => {
                return {
                    ...state,
                    orderBobsDataLoading: false,
                    orderBobsData: action.payload,
                    orderBobsDataError: null
                } as ReduxStateWorkorderManagement;
            })
            .addCase(getWorkorderManagementBobsRedux.rejected, (state, action) => {
                return {
                    ...state,
                    orderBobsData: [],
                    orderBobsDataLoading: false,
                    orderBobsDataError: action.error.code === "ERR_CANCELED" ? null : action.payload
                } as ReduxStateWorkorderManagement;
            });

        builder
            .addCase(deleteWorkorderManagementBobRedux.pending, state => {
                return {
                    ...state,
                    orderBobsDataLoading: true
                } as ReduxStateWorkorderManagement;
            })
            .addCase(deleteWorkorderManagementBobRedux.fulfilled, (state, action) => {
                return {
                    ...state,
                    orderBobsDataLoading: false,
                    orderBobsData: action.payload,
                    orderBobsDataError: null
                } as ReduxStateWorkorderManagement;
            })
            .addCase(deleteWorkorderManagementBobRedux.rejected, (state, action) => {
                return {
                    ...state,
                    orderBobsDataLoading: false,
                    orderBobsDataError: action.error.code === "ERR_CANCELED" ? null : action.payload
                } as ReduxStateWorkorderManagement;
            });

        builder
            .addCase(changeWorkorderManagementBobPriorityRedux.pending, state => {
                return {
                    ...state
                } as ReduxStateWorkorderManagement;
            })
            .addCase(changeWorkorderManagementBobPriorityRedux.fulfilled, (state, action) => {
                return {
                    ...state,
                    orderBobsDataLoading: false,
                    orderBobsData: action.payload,
                    orderBobsDataError: null,
                    orderBobAdding: null
                } as ReduxStateWorkorderManagement;
            })
            .addCase(changeWorkorderManagementBobPriorityRedux.rejected, (state, action) => {
                return {
                    ...state,
                    orderBobsDataLoading: false,
                    orderBobsDataError: action.error.code === "ERR_CANCELED" ? null : action.payload
                } as ReduxStateWorkorderManagement;
            });

        builder
            .addCase(addWorkorderManagementBobRedux.pending, state => {
                return {
                    ...state
                } as ReduxStateWorkorderManagement;
            })
            .addCase(addWorkorderManagementBobRedux.fulfilled, (state, action) => {
                return {
                    ...state,
                    orderBobsDataLoading: false,
                    orderBobsData: action.payload,
                    orderBobsDataError: null,
                    orderBobAdding: null
                } as ReduxStateWorkorderManagement;
            })
            .addCase(addWorkorderManagementBobRedux.rejected, (state, action) => {
                return {
                    ...state,
                    orderBobsDataLoading: false,
                    orderBobsDataError: action.error.code === "ERR_CANCELED" ? null : action.payload
                } as ReduxStateWorkorderManagement;
            });

        builder
            .addCase(alternatingWorkordersData.pending, state => {
                return {
                    ...state,
                    alternatingWokrordersDataLoading: true,
                    alternatingWokrordersDataError: null
                } as ReduxStateWorkorderManagement;
            })
            .addCase(alternatingWorkordersData.fulfilled, (state, action) => {
                return {
                    ...state,
                    alternatingWokrordersDataLoading: false,
                    alternatingWokrordersData: action.payload,
                    alternatingWokrordersDataError: null
                } as ReduxStateWorkorderManagement;
            })
            .addCase(alternatingWorkordersData.rejected, (state, action) => {
                return {
                    ...state,
                    alternatingWokrordersDataLoading: false,
                    alternatingWokrordersDataError: action.error.code === "ERR_CANCELED" ? null : action.payload
                } as ReduxStateWorkorderManagement;
            });

        builder
            .addCase(testPrinters.pending, state => {
                return {
                    ...state
                } as ReduxStateWorkorderManagement;
            })
            .addCase(testPrinters.fulfilled, (state, action) => {
                return {
                    ...state,
                    testPrintPrinters: action.payload
                } as ReduxStateWorkorderManagement;
            })
            .addCase(testPrinters.rejected, (state, action) => {
                return {
                    ...state
                } as ReduxStateWorkorderManagement;
            });

        builder
            .addCase(getWorkorderLabelRedux.pending, state => {
                return { ...state, labelsDataLoading: true } as ReduxStateWorkorderManagement;
            })
            .addCase(getWorkorderLabelRedux.fulfilled, (state, action) => {
                return {
                    ...state,
                    labelsDataLoading: false,
                    labelsData: action.payload,
                    labelsDirty: false
                } as ReduxStateWorkorderManagement;
            })
            .addCase(getWorkorderLabelRedux.rejected, (state, action) => {
                return {
                    ...state,
                    labelsDataLoading: false
                } as ReduxStateWorkorderManagement;
            });

        builder
            .addCase(saveWorkorderLabelRedux.pending, state => {
                return { ...state, labelsDataLoading: true } as ReduxStateWorkorderManagement;
            })
            .addCase(saveWorkorderLabelRedux.fulfilled, (state, action) => {
                return {
                    ...state,
                    labelsDataLoading: false,
                    labelsData: action.payload,
                    labelsDirty: false
                } as ReduxStateWorkorderManagement;
            })
            .addCase(saveWorkorderLabelRedux.rejected, (state, action) => {
                return {
                    ...state,
                    labelsDataLoading: false
                } as ReduxStateWorkorderManagement;
            });
    }
});

export const {
    onClearWorkorderManagementData,
    onSetSelectedOperations,
    onSetSelectedBatches,
    onSetSelectedBobs,
    onSetSelectedBoms,
    onSetWorkorderBatchesData,
    onUpdateWorkorderBatch,
    onSetWorkorderBobsData,
    onSetWorkorderBomsData,
    onSetWorkorderMainData,
    onSetWorkorderMaterialData,
    onSetWorkorderOperationsData,
    onClearAlternatingWorkorderData,
    onSetWorkorderDataDirty,
    onSetWorkorderMaterialDataDirty,
    onSetBatchesAdding,
    onSetBomsAdding,
    onSetBatchesAddingMultiple,
    onSetBatchesAddingMultipleDays,
    onSetTestPrintLabelId,
    onSetTestPrintPrinterId,
    onSetWorkorderLabels,
    onAddWorkorderBatch,
    setOrderBobAddingRedux
} = workorderManagement.actions;
export default workorderManagement.reducer;
